import React, { useMemo, useState } from "react";
import { flip, offset, shift } from "@floating-ui/dom";
import { Tooltip } from "react-tooltip";
import { useQuery } from "urql";
import { Link } from "wouter";
import "react-tooltip/dist/react-tooltip.css";

import { DateHelper } from "../../../../common/src/calendar/DateHelper";
import { numberFormatFun } from "../../../../common/src/utils/FormatFunctions";
import { CopyToClipboardButton } from "../../../../components/src/CopyToClipboardButton";
import { useQueryState } from "../../../../components/src/use-query-state";
import { getPivotInstrumentReturns } from "../../../../graphql/src/performance/queries";
import { CurrencyEnum, Party } from "../../types.generated";
import { PerformancePeriodSelector } from "./PerformancePeriodSelector";
import {
    buildInstrumentRows,
    instrumentRowsToTsv,
    periodCoverage,
    stockReturnFromTimeSeries,
    valueOnOrBefore,
    InstrumentTableRow,
    InstrumentTimeSeries,
    StockPeriodReturn
} from "./sectorTable";

const pctFormat = numberFormatFun("0.00%");
const SEK = CurrencyEnum.SEK;

interface Props {
    client: Partial<Party>;
    data: any;
    endDate: string;
}

interface CloseSeries {
    dates?: string[];
    values?: Array<string | number>;
}

const formatPct = (value: number | null): string => {
    if (value === null || typeof value === "undefined" || Number.isNaN(value)) {
        return "";
    }
    return pctFormat(value) ?? "";
};

const headerClass = "px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap";
const cellClass = "px-3 py-2 tabular-nums";

const TOOLTIP_ID = "sector-table-tooltip";

const tooltipProps = (content: string) => ({
    "data-tooltip-id": TOOLTIP_ID,
    "data-tooltip-content": content,
    "data-tooltip-delay-show": 500
});

const fxInstrumentName = (currency: string): string => `${currency}${SEK}`;

// FX rates are fetched from before the start date so a start rate is still found when the period opens on a
// holiday or a day the pair did not trade. Return(Total) needs no lookback: the query asks for the last
// observation on or before the start date separately.
const FX_LOOKBACK_DAYS = 10;

export function PerformanceSectors({ client, data, endDate }: Props): React.ReactElement {
    const [startDate] = useQueryState("startDate", client.firstTradeDate);
    const [clipboard, setClipboard] = useState<string | null>(null);

    // The table rows without returns. Only instruments held during the period survive buildInstrumentRows,
    // so deriving the query variables from these keeps the total return fetch scoped to what is on screen
    // instead of every instrument the fund has ever held.
    const baseRows: InstrumentTableRow[] = useMemo(() => {
        const performance = data?.performance;
        if (!performance || !Array.isArray(performance.instrumentPerformances)) {
            return [];
        }
        return buildInstrumentRows({
            dates: performance.dates ?? [],
            values: performance.values ?? [],
            series: performance.series ?? [],
            instrumentPerformances: performance.instrumentPerformances,
            stockReturnsByInstrumentId: {},
            startDate,
            endDate,
            partyInstrumentIds: (data?.client?.instruments ?? []).map((instrument) => String(instrument._id))
        });
    }, [data, endDate, startDate]);

    const { instrumentIds, currencyByInstrumentId, fxNames } = useMemo(() => {
        const instrumentIds: string[] = [];
        const currencyByInstrumentId: Record<string, string> = {};
        const fxNames: string[] = [];
        const fxSeen: Record<string, boolean> = {};
        for (const row of baseRows) {
            if (!row.instrumentId || row.instrumentId in currencyByInstrumentId) {
                continue;
            }
            const currency = row.currency || SEK;
            instrumentIds.push(row.instrumentId);
            currencyByInstrumentId[row.instrumentId] = currency;
            if (currency !== SEK) {
                const name = fxInstrumentName(currency);
                if (!fxSeen[name]) {
                    fxSeen[name] = true;
                    fxNames.push(name);
                }
            }
        }
        return { instrumentIds, currencyByInstrumentId, fxNames };
    }, [baseRows]);

    const fxStartDate = useMemo(() => {
        if (!startDate) {
            return startDate;
        }
        return DateHelper.dateToString(DateHelper.dateAddDays(new Date(`${startDate}T12:00:00.000Z`), -FX_LOOKBACK_DAYS));
    }, [startDate]);

    const [{ fetching, error, data: priceData }] = useQuery({
        query: getPivotInstrumentReturns,
        variables: { instrumentIds, fxNames, startDate, fxStartDate, endDate },
        pause: !instrumentIds.length || !startDate || !endDate,
        requestPolicy: "cache-and-network"
    });

    const stockReturnsByInstrumentId: Record<string, StockPeriodReturn> = useMemo(() => {
        const fxByName: Record<string, CloseSeries> = {};
        for (const series of priceData?.fx ?? []) {
            const name = series.instrument?.name;
            if (name) {
                fxByName[name] = series;
            }
        }
        const lastDateByInstrumentId = (seriesList: any[]): Record<string, string> => {
            const byId: Record<string, string> = {};
            for (const series of seriesList ?? []) {
                const id = series.instrumentId ? String(series.instrumentId) : "";
                const dates = series.dates ?? [];
                if (id && dates.length) {
                    byId[id] = String(dates[dates.length - 1]);
                }
            }
            return byId;
        };
        // The observation the period starts from, and the last observation the series has at all.
        const anchorByInstrumentId = lastDateByInstrumentId(priceData?.anchor);
        const latestByInstrumentId = lastDateByInstrumentId(priceData?.latest);
        // One instrument yields up to two rows here, Return(Total) and Price(Close).
        const seriesByInstrumentId: Record<string, InstrumentTimeSeries[]> = {};
        for (const series of priceData?.prices ?? []) {
            const id = series.instrumentId ? String(series.instrumentId) : "";
            if (!id) {
                continue;
            }
            if (!seriesByInstrumentId[id]) {
                seriesByInstrumentId[id] = [];
            }
            seriesByInstrumentId[id].push(series);
        }
        const byId: Record<string, StockPeriodReturn> = {};
        // Every instrument that was queried gets an entry, also the ones without a Return(Total) series, so a
        // blank cell can say whether the instrument has no series at all or one that misses the period.
        for (const id of instrumentIds) {
            const { perf, coveredFrom, coveredTo } = stockReturnFromTimeSeries(
                seriesByInstrumentId[id],
                startDate,
                endDate,
                anchorByInstrumentId[id] ?? null
            );
            const currency = currencyByInstrumentId[id] || SEK;
            let sekPerf = perf;
            if (currency !== SEK) {
                // Over the period the return covers, not the one that was asked for, so a partly covered
                // return is not converted with an FX move from days it does not include.
                const fx = fxByName[fxInstrumentName(currency)];
                const fxStart = fx ? valueOnOrBefore(fx.dates ?? [], fx.values ?? [], coveredFrom ?? startDate) : null;
                const fxEnd = fx ? valueOnOrBefore(fx.dates ?? [], fx.values ?? [], coveredTo ?? endDate) : null;
                if (perf !== null && fxStart && fxEnd && fxStart !== 0) {
                    sekPerf = (1 + perf) * (fxEnd / fxStart) - 1;
                } else {
                    sekPerf = null;
                }
            }
            byId[id] = { perf, sekPerf, coveredFrom, coveredTo, latestDate: latestByInstrumentId[id] ?? null };
        }
        return byId;
    }, [currencyByInstrumentId, endDate, instrumentIds, priceData, startDate]);

    // Kept separate from baseRows so an arriving price payload does not redo the attribution walk.
    const rows: InstrumentTableRow[] = useMemo(() => {
        return baseRows.map((row) => {
            const stockReturn = row.instrumentId ? stockReturnsByInstrumentId[row.instrumentId] : undefined;
            return {
                ...row,
                sekPerf: stockReturn?.sekPerf ?? null,
                perf: stockReturn?.perf ?? null,
                perfFrom: stockReturn?.coveredFrom ?? null,
                perfTo: stockReturn?.coveredTo ?? null,
                perfLatest: stockReturn?.latestDate ?? null
            };
        });
    }, [baseRows, stockReturnsByInstrumentId]);

    const returnCell = (value: number | null, row: InstrumentTableRow, inSek: boolean): React.ReactElement => {
        if (value === null && !priceData) {
            // Still loading, which the line above the table already says.
            return <></>;
        }
        if (value !== null) {
            // Amber whenever the return is not measured on the period exactly, and withheld altogether when it
            // is measured too far from it to pass for the period return. The tooltip carries the dates.
            const coverage = periodCoverage(row.perfFrom, row.perfTo, startDate, endDate);
            if (coverage.match === "off") {
                return (
                    <span className="cursor-pointer font-semibold text-amber-600" {...tooltipProps(coverage.note)}>
                        *
                    </span>
                );
            }
            if (coverage.match === "close") {
                return (
                    <span className="cursor-pointer text-amber-600" {...tooltipProps(coverage.note)}>
                        {formatPct(value)}
                    </span>
                );
            }
            return <>{formatPct(value)}</>;
        }
        let reason = `No total return data in the selected period, the series ends ${row.perfLatest}.`;
        if (inSek && row.perf !== null) {
            reason = `No ${fxInstrumentName(row.currency || SEK)} rate for the period, so the local return cannot be converted.`;
        } else if (!row.perfLatest) {
            reason = "This instrument has no Return(Total) series.";
        }
        return (
            <span className="cursor-pointer text-slate-400" {...tooltipProps(reason)}>
                –
            </span>
        );
    };

    if (!client.firstTradeDate) {
        return <p>Missing firstTradeDate</p>;
    }

    // A partial failure, e.g. one unreadable series, still leaves the rest of the payload usable, so the table
    // is only replaced by the error when nothing came back at all.
    if (error && !priceData) {
        return (
            <div>
                <p>Error loading total returns: {error.message}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-1 bg-gray-100/90 backdrop-blur border-b border-slate-200 print-none">
                <div className="flex flex-wrap items-center gap-3">
                    <PerformancePeriodSelector firstAvailableStartDate={client.firstTradeDate} endDate={endDate} />
                    <div className="flex items-center ml-auto">
                        <CopyToClipboardButton
                            id="sector-holdings-copy"
                            clipboard={clipboard}
                            title="Copy to clipboard as tab-separated data."
                            onClick={() => setClipboard(instrumentRowsToTsv(rows, startDate, endDate))}
                        />
                    </div>
                </div>
            </div>

            {fetching && !priceData ? <p>Loading total returns...</p> : null}
            {error ? <p className="text-amber-700">Some total returns could not be loaded: {error.message}</p> : null}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-sky-50 border-b border-sky-100">
                        <tr>
                            <th className={headerClass}>Instrument</th>
                            <th className={headerClass}>Ticker</th>
                            <th className={headerClass}>Model type</th>
                            <th className={headerClass}>Sector</th>
                            <th className={headerClass}>Currency</th>
                            <th className={`${headerClass} text-right`}>Weight Start</th>
                            <th className={`${headerClass} text-right`}>Weight End</th>
                            <th className={`${headerClass} text-right`}>Contributing</th>
                            <th className={`${headerClass} text-right`}>
                                <span
                                    className="cursor-pointer"
                                    {...tooltipProps(
                                        "Total return in portfolio currency for the period when the instrument was in position"
                                    )}
                                >
                                    Total return
                                </span>
                            </th>
                            <th className={`${headerClass} text-right`}>
                                <span
                                    className="cursor-pointer"
                                    {...tooltipProps(
                                        "Total return performance in SEK for the given period, only available when total return timeseries exists for the instrument."
                                    )}
                                >
                                    Performance (SEK)
                                </span>
                            </th>
                            <th className={`${headerClass} text-right`}>
                                <span
                                    className="cursor-pointer"
                                    {...tooltipProps(
                                        "Total return performance in local currency for the given period, only available when total return timeseries exists for the instrument."
                                    )}
                                >
                                    Performance (local)
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td className="px-3 py-4 text-slate-500" colSpan={11}>
                                    No positions in the selected period.
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.key} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {row.instrumentId ? (
                                            <Link to={`/instruments/${row.instrumentId}`} target="_blank">
                                                {row.name}
                                            </Link>
                                        ) : (
                                            row.name
                                        )}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">{row.bloombergTicker}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{row.modelType}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{row.sector}</td>
                                    <td className="px-3 py-2">{row.currency}</td>
                                    <td className={`${cellClass} text-right`}>{formatPct(row.weightStart)}</td>
                                    <td className={`${cellClass} text-right`}>{formatPct(row.weightEnd)}</td>
                                    <td className={`${cellClass} text-right`}>{formatPct(row.contributing)}</td>
                                    <td className={`${cellClass} text-right`}>{formatPct(row.accountPerf)}</td>
                                    <td className={`${cellClass} text-right`}>{returnCell(row.sekPerf, row, true)}</td>
                                    <td className={`${cellClass} text-right`}>{returnCell(row.perf, row, false)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Rendered outside the scrolling table container, and with a z-index above the sticky toolbar
                (react-tooltip ships no z-index), so a tooltip placed above a header cell stays visible. */}
            <Tooltip
                id={TOOLTIP_ID}
                place="top"
                positionStrategy="fixed"
                middlewares={[offset(6), flip(), shift({ padding: 8 })]}
                style={{ zIndex: 30, maxWidth: 300, whiteSpace: "pre-line" }}
            />
        </div>
    );
}
